// services/DonationService.js - ReactJS (web)
import api from '../api/api';

class DonationService {
  // ==================== PUBLIC ROUTES ====================

  /**
   * Create donation with Stripe payment intent
   * POST /api/donations/create-payment
   */
  async createDonationWithPayment(donationData) {
    try {
      const response = await api.post('/donations/create-payment', donationData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }


    /**
   * Confirm successful Stripe payment
   * POST /api/donations/confirm-payment
   *
   * The backend verifies the PaymentIntent directly with Stripe
   * before changing the donation status to completed.
   */
  async confirmPayment(paymentIntentId) {
    try {
      if (!paymentIntentId) {
        throw new Error('Payment Intent ID is required');
      }

      console.log(
        '🔐 Confirming Stripe payment:',
        paymentIntentId
      );

      const response = await api.post(
        '/donations/confirm-payment',
        {
          paymentIntentId
        }
      );

      console.log(
        '✅ Stripe payment confirmation response:',
        response.data
      );

      return response.data;

    } catch (error) {
      console.error(
        '❌ Failed to confirm Stripe payment:',
        error
      );

      throw this.handleError(error);
    }
  }

  /**
   * Create donation with Stripe Checkout Session
   * POST /api/donations/create-checkout
   */
  async createDonationWithCheckout(donationData) {
    try {
      const response = await api.post('/donations/create-checkout', donationData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation by Stripe session ID
   * GET /api/donations/session/:sessionId
   */
  async getDonationBySessionId(sessionId) {
    try {
      const response = await api.get(`/donations/session/${sessionId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation by Stripe payment ID
   * GET /api/donations/payment/:paymentId
   */
  async getDonationByPaymentId(paymentId) {
    try {
      const response = await api.get(`/donations/payment/${paymentId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation by donation number
   * GET /api/donations/number/:donationNumber
   */
  async getDonationByNumber(donationNumber) {
    try {
      const response = await api.get(`/donations/number/${donationNumber}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get public donations (not hidden)
   * GET /api/donations/public
   */
  async getPublicDonations(params = {}) {
    try {
      const response = await api.get('/donations/public', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get featured donations
   * GET /api/donations/featured
   */
  async getFeaturedDonations() {
    try {
      const response = await api.get('/donations/featured');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation purposes (for dropdowns)
   * GET /api/donations/purposes
   */
  async getDonationPurposes() {
    try {
      const response = await api.get('/donations/purposes');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation statuses (for dropdowns)
   * GET /api/donations/statuses
   */
  async getDonationStatuses() {
    try {
      const response = await api.get('/donations/statuses');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation currencies (for dropdowns)
   * GET /api/donations/currencies
   */
  async getDonationCurrencies() {
    try {
      const response = await api.get('/donations/currencies');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== ADMIN ROUTES ====================

  /**
   * Get all donations (admin only)
   * GET /api/donations
   */
  async getAllDonations(params = {}) {
    try {
      const response = await api.get('/donations', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation by ID (admin only)
   * GET /api/donations/:id
   */
  async getDonationById(id) {
    try {
      const response = await api.get(`/donations/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donations by donor email
   * GET /api/donations/donor/:email
   */
  async getDonationsByDonor(email, params = {}) {
    try {
      const response = await api.get(`/donations/donor/${email}`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Confirm donation (for manual payments)
   * PATCH /api/donations/:id/confirm
   */
  async confirmDonation(id) {
    try {
      const response = await api.patch(`/donations/${id}/confirm`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Refund donation (Stripe)
   * POST /api/donations/:id/refund
   */
  async refundDonation(id, reason = '') {
    try {
      const response = await api.post(`/donations/${id}/refund`, { reason });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Send thank you email to donor
   * POST /api/donations/:id/thank-you
   */
  async sendThankYou(id) {
    try {
      const response = await api.post(`/donations/${id}/thank-you`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add admin note to donation
   * POST /api/donations/:id/note
   */
  async addAdminNote(id, note) {
    try {
      const response = await api.post(`/donations/${id}/note`, { note });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== SUPER ADMIN ROUTES ====================

  /**
   * Get donation analytics
   * GET /api/donations/analytics
   */
  async getDonationAnalytics(startDate, endDate) {
    try {
      const response = await api.get('/donations/analytics', {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation trends
   * GET /api/donations/trends
   */
  async getDonationTrends(days = 30) {
    try {
      const response = await api.get('/donations/trends', {
        params: { days }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get today's donations
   * GET /api/donations/today
   */
  async getTodayDonations() {
    try {
      const response = await api.get('/donations/today');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation statistics
   * GET /api/donations/statistics
   */
  async getDonationStatistics() {
    try {
      const response = await api.get('/donations/statistics');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    let errorMessage = ' ';
    
    if (error.response?.data?.data?.error?.message) {
      errorMessage = error.response.data.data.error.message;
    } else if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status,
      data: error.response?.data
    };
  }

  /**
   * Get donation purposes (local fallback)
   */
  getDonationPurposesLocal() {
    return {
      platform_support: 'Platform Support',
      community_development: 'Community Development',
      technical_support: 'Technical Support',
      general: 'General',
      other: 'Other'
    };
  }

  /**
   * Get donation statuses (local fallback)
   */
  getDonationStatusesLocal() {
    return {
      pending: 'Pending',
      completed: 'Completed',
      failed: 'Failed',
      refunded: 'Refunded'
    };
  }

  /**
   * Get currency options (local fallback)
   */
  getCurrencyOptionsLocal() {
    return {
      USD: 'US Dollar',
      EUR: 'Euro',
      HTG: 'Haitian Gourde',
      CAD: 'Canadian Dollar'
    };
  }

  /**
   * Format donation amount
   */
  formatAmount(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  /**
   * Get status badge color
   */
  getStatusColor(status) {
    const colors = {
      pending: '#FFA500',
      completed: '#2ECC71',
      failed: '#E74C3C',
      refunded: '#95A5A6'
    };
    return colors[status] || '#95A5A6';
  }

  /**
   * Get status label
   */
  getStatusLabel(status) {
    const labels = {
      pending: 'Pending',
      completed: 'Completed',
      failed: 'Failed',
      refunded: 'Refunded'
    };
    return labels[status] || status;
  }

  /**
   * Validate donation data
   */
  validateDonationData(data) {
    const errors = [];

    if (!data.amount || data.amount <= 0) {
      errors.push('Donation amount must be greater than 0');
    }

    if (!data.donorName) {
      errors.push('Donor name is required');
    } else if (data.donorName.length < 2) {
      errors.push('Donor name must be at least 2 characters');
    }

    if (!data.donorEmail) {
      errors.push('Donor email is required');
    } else if (!this.isValidEmail(data.donorEmail)) {
      errors.push('Invalid email format');
    }

    if (data.message && data.message.length > 500) {
      errors.push('Message cannot exceed 500 characters');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate email format
   */
  isValidEmail(email) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  /**
   * Format donation for display
   */
  formatDonation(donation) {
    if (!donation) return null;

    return {
      ...donation,
      id: donation._id,
      statusLabel: this.getStatusLabel(donation.status),
      statusColor: this.getStatusColor(donation.status),
      formattedAmount: this.formatAmount(donation.amount, donation.currency),
      createdAt: donation.createdAt ? new Date(donation.createdAt) : null,
      completedAt: donation.completedAt ? new Date(donation.completedAt) : null,
      refundedAt: donation.refundedAt ? new Date(donation.refundedAt) : null,
      timeAgo: donation.createdAt ? this.getTimeAgo(donation.createdAt) : null,
      isAnonymous: donation.isAnonymous || false,
      isCompleted: donation.status === 'completed',
      isRefunded: donation.status === 'refunded',
      isFailed: donation.status === 'failed'
    };
  }

  /**
   * Get time ago string
   */
  getTimeAgo(date) {
    if (!date) return null;
    const now = new Date();
    const diff = now - new Date(date);
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (weeks < 4) return `${weeks}w ago`;
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  }

  /**
   * Build query params for donation search
   */
  buildQueryParams(filters = {}) {
    const params = {};

    if (filters.status) {
      params.status = filters.status;
    }

    if (filters.purpose) {
      params.purpose = filters.purpose;
    }

    if (filters.donorEmail) {
      params.donorEmail = filters.donorEmail;
    }

    if (filters.minAmount) {
      params.minAmount = filters.minAmount;
    }

    if (filters.maxAmount) {
      params.maxAmount = filters.maxAmount;
    }

    if (filters.fromDate) {
      params.fromDate = filters.fromDate;
    }

    if (filters.toDate) {
      params.toDate = filters.toDate;
    }

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.page) {
      params.page = filters.page;
    }

    if (filters.limit) {
      params.limit = filters.limit;
    }

    if (filters.sortBy) {
      params.sortBy = filters.sortBy;
    }

    if (filters.sortOrder) {
      params.sortOrder = filters.sortOrder;
    }

    return params;
  }

  /**
   * Calculate donation progress
   */
  calculateProgress(current, target) {
    if (!target || target <= 0) return 0;
    return Math.min(((current / target) * 100), 100);
  }

  /**
   * Get donation summary from list
   */
  getDonationSummary(donations) {
    if (!donations || donations.length === 0) {
      return {
        total: 0,
        count: 0,
        average: 0,
        max: 0,
        min: 0
      };
    }

    const total = donations.reduce((sum, d) => sum + (d.amount || 0), 0);
    const count = donations.length;
    const average = total / count;
    const max = Math.max(...donations.map(d => d.amount || 0));
    const min = Math.min(...donations.map(d => d.amount || 0));

    return {
      total,
      count,
      average,
      max,
      min
    };
  }
}

export default new DonationService();